import {Directive, ElementRef, forwardRef, HostListener, Input, Renderer2, OnDestroy} from '@angular/core';
import {DefaultValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {TranslateService} from '@ngx-translate/core';

@UntilDestroy()
@Directive({
    selector: 'input[mydocma-time-field-input]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            // When Angular is setting up dependency injection it sometimes needs
            // to refer to classes or tokens that aren't yet fully defined
            // forwardRef gives chance to angualr it will be defined later
            useExisting: forwardRef(() => TimeFieldInputDirective),
            multi: true
        }
    ]
})
export class TimeFieldInputDirective extends DefaultValueAccessor implements OnDestroy {
    private touchEndListener: () => void;
    private englishMeridian: 'AM' | 'PM' = 'AM';

    constructor(
        private readonly renderer: Renderer2,
        private readonly elementRef: ElementRef<any>,
        private readonly translateService: TranslateService
    ) {
        super(renderer, elementRef, false);

        // Add touchend listener
        if (window.matchMedia('(pointer: coarse)').matches) {
            this.touchEndListener = this.renderer.listen(
                this.elementRef.nativeElement,
                'touchend',
                (event: TouchEvent) => this.handleTouchEnd(event)
            );
        }

        this.translateService.onLangChange
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                const controlValue = this.elementRef.nativeElement.value;
                const formattedValue = this.formatTimeValue(controlValue);
                if (formattedValue && formattedValue !== controlValue) {
                    super.writeValue(formattedValue);
                }
            });
    }

    @HostListener('input', ['$event.currentTarget.value'])
    onInput = (event: any) => {};

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent): void {
        if(!this.elementRef.nativeElement.classList.contains('mydocma-time-field-text-input')) return;

        const input = this.elementRef.nativeElement;
        const inputValue = input.value;
        
        if(!inputValue || !inputValue.includes(':')) {
            return;
        }
        
        const colonPosition = inputValue.indexOf(':');
        const cursorPosition = input.selectionStart;
        const meridiemPosition = inputValue.includes('AM') ? inputValue.indexOf(' AM') : inputValue.indexOf(' PM');
        
        if (cursorPosition <= colonPosition) {
            // User clicked on hours part -> select hours part
            setTimeout(() => this.elementRef.nativeElement.setSelectionRange(0, 2), 0);
        } else if (meridiemPosition !== -1 && cursorPosition <= meridiemPosition) {
            // User clicked on minutes part -> select minutes part
            setTimeout(() => this.elementRef.nativeElement.setSelectionRange(3, 5), 0);
        } else {
            // User clicked on AM/PM part -> select AM/PM part
            setTimeout(() => this.elementRef.nativeElement.setSelectionRange(6, 8), 0);
        }
    }

    private handleTouchEnd(event: TouchEvent): void {
        setTimeout(() => {
            const input = this.elementRef.nativeElement;
            const inputValue = input.value;
            
            if(!inputValue || !inputValue.includes(':')) {
                return;
            }

            if(input.selectionStart === input.selectionEnd) {
                if(input.selectionStart === 0) {
                    this.elementRef.nativeElement.setSelectionRange(0, 2);
                } else {
                    this.elementRef.nativeElement.setSelectionRange(3, 5);
                }
            }
        }, 100);
    }

    // When the input loses focus, format the input value
    @HostListener('blur', ['$event'])
    onBlur($event: FocusEvent): void {
        if(!this.elementRef.nativeElement.classList.contains('mydocma-time-field-text-input')) return;
        const currentValue = this.elementRef.nativeElement.value;
        const finalValue = this.formatTimeValue(currentValue);

        if(finalValue && finalValue !== currentValue) {
            super.writeValue(finalValue);
            this.onInput(finalValue);
        }
    }

    // BeforeInput serves basically as a keydown listener here: Virtual keyboards on android do not detect keydown events like normal keyboards
    @HostListener('beforeinput', ['$event'])
    onBeforeInput($event): void {
        this.onKeyDown({key: $event.data} as KeyboardEvent);
    }

    onKeyDown($event: KeyboardEvent): void {
        if(!this.elementRef.nativeElement.classList.contains('mydocma-time-field-text-input')) return;
       
        // This logic handles where the user mouse position is moved to when user enters time
        // See subtickets of RPW-4011 for details
        const selectionStart = this.elementRef.nativeElement.selectionStart;
        const selectionEnd = this.elementRef.nativeElement.selectionEnd;
        const selectionLength = selectionEnd - selectionStart;
        const inputValue = this.elementRef.nativeElement.value;
        const inputKey = $event.key;
        let newSelectionStart = -1;
        let newSelectionEnd = -1;

        // If there's an active selection, this logic determines where the cursor should go after input
        if (selectionLength > 0) {
            // If the selection starts at the beginning (hours part)
            if (selectionStart === 0) {
                // If the typed key is a number greater than 2, it implies the user is skipping to minutes
                const maxFirstHourDigit = this.isEnglishLanguage() ? 1 : 2;
                if (!isNaN(Number(inputKey)) && Number(inputKey) > maxFirstHourDigit) {
                    newSelectionStart = 3; // Move to minutes start
                    newSelectionEnd = 5;   // Select minutes end
                } else {
                    // If a key is typed (not null, i.e., not a delete/backspace)
                    if (inputKey !== null) {
                        if (selectionStart === 0) {
                            newSelectionStart = 2; // Select second hour digit for overwrite
                            newSelectionEnd = 2;
                        } else {
                            newSelectionStart = 3; // Hour completed -> jump to minutes
                            newSelectionEnd = 5;
                        }
                    } else {
                        newSelectionStart = 0; // Select hours again (e.g., after backspace)
                        newSelectionEnd = 2;   // Select hours again
                    }
                }
            }
            // If the selection is at the meridian part
            else if (selectionStart > 5) {
                if(inputKey === 'A' || inputKey === 'a') {
                    this.englishMeridian = 'AM';
                } else if(inputKey === 'P' || inputKey === 'p') {
                    this.englishMeridian = 'PM';
                }
                newSelectionStart = 6; // Move to AM/PM end
                newSelectionEnd = 8;   // No selection
            } else {
                newSelectionStart = 5; // Move to AM/PM start
                newSelectionEnd = 5; // No selection
            }
        } else { 
            // No active selection, determine cursor position based on current input and key
            // Case 1: Input is empty, and user types a valid first hour digit (0, 1, or 2)
            // Position cursor before the colon to allow for a second hour digit
            const maxFirstHourDigit = this.isEnglishLanguage() ? 1 : 2;
            if (inputValue.length === 0 && !isNaN(Number(inputKey)) && Number(inputKey) >= 0 && Number(inputKey) <= maxFirstHourDigit) {
                newSelectionStart = 2;
                newSelectionEnd = 2;
            // Case 2: Input has one digit, first digit is > 2, and user types another digit
            // Prevent second hour digit and move to minutes section
            } else if (inputValue.length === 1 && Number(inputValue.charAt(0)) > 2 && !isNaN(Number(inputKey))) {
                newSelectionStart = 3;   // Move to minutes start
                newSelectionEnd = 5;     // Select minutes end
            // Case 3: Cursor is at index 2 (before the colon)
            } else if (selectionStart === 2) {
                // If a key is typed (not null, i.e., not a delete/backspace)
                if (inputKey !== null) {
                    newSelectionStart = 3; // Move to minutes start
                    newSelectionEnd = 5;   // Select minutes end
                } else {
                    newSelectionStart = 0; // Select hours again (e.g., after backspace)
                    newSelectionEnd = 2;   // Select hours again
                }
            // Case 4: Cursor is at index 5 (end of minutes)
            } else if (selectionStart >= 5 && this.isEnglishLanguage()) {
                // If the value ends with :00, re-select the minutes to allow easy overwriting
                newSelectionStart = 6; // Select minutes start
                newSelectionEnd = 8;   // Select minutes end
            }
        }

        if (newSelectionStart !== -1) {
            setTimeout(() => this.elementRef.nativeElement.setSelectionRange(newSelectionStart, newSelectionEnd), 0);
        }
    }
    

    @HostListener('paste', ['$event'])
    onPaste($event: ClipboardEvent): void {
        const clipboardValue = $event.clipboardData.getData('text/plain');
        if (!clipboardValue) {
            return;
        }

        const formattedValue = this.formatTimeValue(clipboardValue);
        if (!formattedValue) {
            $event.preventDefault();
            return;
        }

        // Prevent the default paste behavior
        $event.preventDefault();
        
        // Manually set the input value
        super.writeValue(formattedValue);
        
        // Trigger the input handler with the formatted value
        this.onInput(formattedValue);
    }

    writeValue(value: any): void {
        const formattedValue = this.formatTimeValue(value);
        super.writeValue(formattedValue);
    }

    registerOnChange(fn: any): void {
        this.onInput = (value) => {
            const formattedValue = this.formatTimeValue(value);
            const modelValue = this.to24HourValue(formattedValue ?? value);

            if (value && value !== formattedValue) {
                super.setProperty('value', formattedValue);
            }

            fn(modelValue ?? formattedValue);
        };
    }

    private formatTimeValue(value: string): string {
        if (!value) {
            return null;
        }

        const parsedTime = this.parseTimeTo24Hour(value);
        if (parsedTime) {
            return this.isEnglishLanguage()
                ? this.format12Hour(parsedTime.hours, parsedTime.minutes)
                : this.format24Hour(parsedTime.hours, parsedTime.minutes);
        }

        // Remove all non-numeric characters except colon
        value = value.replace(/[^\d:]/g, '');

        // If user deleted some parts of the time, fill in zeros
        if (value.length < 5) {
            const parts = value.split(':');
            let hours = parts[0] || '00';
            let minutes = parts[1] || '00';

            // Pad hours and minutes to 2 digits
            hours = hours.padStart(2, '0');
            minutes = minutes.padStart(2, '0');

            value = `${hours}:${minutes}`;
        }

        // Remove characters if more than 2 characters are set as hours/minutes
        if (value.length > 5) {
            const parts = value.split(':');
            if (parts[0].length > 2) {
                parts[0] = parts[0].slice(-2);
            }
            if (parts[1].length > 2) {
                parts[1] = parts[1].slice(-2); 
            }
            value = parts[0] + ':' + parts[1];
        }
        

        let hours = '';
        let minutes = '';
        
        if(value.includes(':')) {
            const parts = value.split(':');
            hours = parts[0].slice(0, 2);
            minutes = parts.length > 1 ? parts[1].slice(0, 2) : '';
        } else {
            // If it's just numbers, format as HHMM
            value = value.slice(0, 4); // Limit to 4 digits
            if (value.length > 2) {
                hours = value.slice(0, 2);
                minutes = value.slice(2);
            } else {
                hours = value;
                minutes = '';
            }
        }

        // Validate hours (0-23)
        if (hours) {
            const hoursNum = parseInt(hours, 10);
            const maxHours = this.isEnglishLanguage() ? 12 : 23;
            if (hoursNum > maxHours) {
                hours = maxHours.toString();
            }
        }

        // Validate minutes (0-59)
        if (minutes) {
            const minutesNum = parseInt(minutes, 10);
            if (minutesNum > 59) {
                minutes = '59';
            }
        }

        // Format the output - add 0s to hours and PM/AM for english language
        if (hours && minutes) {            
            if(hours.length === 1) {
                const hoursPart = hours.padStart(2, '0');
                return this.isEnglishLanguage() ? `${hoursPart}:${minutes} ${this.englishMeridian}` : `${hoursPart}:${minutes}`;
            } else {
                return this.isEnglishLanguage() ? `${hours}:${minutes} ${this.englishMeridian}` : `${hours}:${minutes}`;
            }
        }
        
        return value;
    }

    private isEnglishLanguage(): boolean {
        return this.translateService.currentLang?.toLowerCase().startsWith('en');
    }

    private parseTimeTo24Hour(value: string): {hours: number; minutes: number} | null {
        const input = value?.trim();
        if (!input) {
            return null;
        }

        const meridianMatch = input.match(/^(\d{1,2}):(\d{1,2})\s*([aApP][mM])$/);
        if (meridianMatch) {
            const meridianHours = Math.min(12, Number(meridianMatch[1]));
            const meridianMinutes = Math.min(59, Number(meridianMatch[2]));
            const meridian = meridianMatch[3].toUpperCase();
            let hours24 = meridianHours % 12;
            if (meridian === 'PM') {
                hours24 += 12;
            }
            return {hours: hours24, minutes: meridianMinutes};
        }

        const timeMatch = input.match(/^(\d{1,2}):(\d{1,2})$/);
        if (!timeMatch) {
            return null;
        }

        return {
            hours: Math.min(23, Number(timeMatch[1])),
            minutes: Math.min(59, Number(timeMatch[2]))
        };
    }

    private format24Hour(hours: number, minutes: number): string {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    private to24HourValue(value: string): string | null {
        const parsedTime = this.parseTimeTo24Hour(value);
        if (!parsedTime) {
            return null;
        }
        return this.format24Hour(parsedTime.hours, parsedTime.minutes);
    }

    private format12Hour(hours: number, minutes: number): string {
        const isPm = hours >= 12;
        const twelveHour = hours % 12 || 12;
        const suffix = isPm ? 'PM' : 'AM';
        return `${twelveHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${suffix}`;
    }

    ngOnDestroy(): void {
        // Remove event listeners
        if (this.touchEndListener) {
            this.touchEndListener();
        }
    }

}
